import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Loader2, Search, Send } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { cn } from "../../lib/utils";

import toastError from "../../errors/toastError";
import api from "../../services/api";

// Encaminhar uma mensagem para outro contato. O texto é reenviado como
// mensagem nova no atendimento do destinatário — não é o "encaminhado" nativo
// do WhatsApp, que a API não expõe.
const ForwardMessageModal = ({ open, onClose, message }) => {
  const [busca, setBusca] = useState("");
  const [contatos, setContatos] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [enviando, setEnviando] = useState(null);

  const procurar = useCallback(async () => {
    if (busca.trim().length < 2) {
      setContatos([]);
      return;
    }

    setBuscando(true);
    try {
      const { data } = await api.get("/contacts", {
        params: { searchParam: busca.trim() },
      });
      setContatos((data.contacts || []).slice(0, 20));
    } catch (err) {
      toastError(err);
    }
    setBuscando(false);
  }, [busca]);

  useEffect(() => {
    // Espera a digitação parar: uma consulta por tecla castigaria o servidor.
    const t = setTimeout(procurar, 400);

    return () => clearTimeout(t);
  }, [procurar]);

  useEffect(() => {
    if (!open) {
      setBusca("");
      setContatos([]);
    }
  }, [open]);

  // O corpo da mensagem enviada por nós vem prefixado com "*Fulano:* ";
  // encaminhar esse prefixo entregaria uma assinatura que não é do
  // destinatário.
  const textoLimpo = String(message?.body || "").replace(/^\*[^*]+:\*\s*/, "");

  // Mídia sem legenda não tem texto para encaminhar. Enviar assim mandaria
  // uma mensagem vazia — foi o que aconteceu no teste.
  const podeEncaminhar = textoLimpo.trim().length > 0;

  const encaminhar = async (contato) => {
    if (!podeEncaminhar) return;

    setEnviando(contato.id);
    try {
      // reaproveitarAberto: cai no atendimento que o contato já tem, se
      // houver. Sem isso ficariam dois tickets abertos para a mesma pessoa
      // e o anterior não reabriria mais (ERR_OTHER_OPEN_TICKET).
      const { data } = await api.post("/tickets", {
        contactId: contato.id,
        status: "open",
        userId: null,
        reaproveitarAberto: true,
      });

      await api.post(`/messages/${data.id}`, {
        body: textoLimpo,
        fromMe: true,
        read: true,
        // Sai marcada como repasse: quem lê a conversa depois precisa saber
        // que aquele texto não foi escrito para aquela pessoa.
        isForwarded: true,
      });

      toast.success(`Encaminhado para ${contato.name}.`);
      onClose();
    } catch (err) {
      toastError(err);
    }
    setEnviando(null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[80vh] flex-col gap-0 p-0 sm:max-w-md">
        <DialogHeader className="border-b px-4 py-3">
          <DialogTitle className="text-base">Encaminhar mensagem</DialogTitle>
          <p className="line-clamp-2 text-xs text-muted-foreground">
            {podeEncaminhar
              ? textoLimpo
              : "Esta mensagem não tem texto para encaminhar."}
          </p>
        </DialogHeader>

        {!podeEncaminhar && (
          <p className="border-b bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
            Só dá para encaminhar mensagens com texto.
          </p>
        )}

        <div className="border-b p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              autoFocus
              placeholder="Buscar contato pelo nome ou número..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 space-y-1 overflow-y-auto p-3">
          {buscando ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : contatos.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {busca.trim().length < 2
                ? "Digite ao menos duas letras para buscar."
                : "Nenhum contato com esse nome."}
            </p>
          ) : (
            contatos.map((contato) => (
              <button
                key={contato.id}
                type="button"
                disabled={!!enviando || !podeEncaminhar}
                onClick={() => encaminhar(contato)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-lg p-2 text-left",
                  "hover:bg-accent disabled:opacity-60",
                )}
              >
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={contato.profilePicUrl} alt={contato.name} />
                  <AvatarFallback>
                    {(contato.name || "?").slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{contato.name}</p>
                  <p className="truncate text-xs tabular-nums text-muted-foreground">
                    {contato.number || "sem número"}
                  </p>
                </div>

                {enviando === contato.id ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
              </button>
            ))
          )}
        </div>

        <DialogFooter className="border-t p-3">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ForwardMessageModal;
